import Cookies from 'js-cookie'
import * as amplitude from '@amplitude/unified'
import { Identify } from '@amplitude/unified'
import type { EnrichmentPlugin } from '@amplitude/analytics-core'
import { logTracking } from '../adapters/helpers'
import { createAnalyticsContext, PageAnalyticsContext } from './helpers'

// Type definitions
interface AmplitudeConfig {
  analytics?: {
    remoteConfig?: {
      fetchRemoteConfig: boolean
    }
    defaultTracking?: boolean | Record<string, boolean>
    userId?: string
    deviceId?: string
    // Invoked before session_start during init(), ensuring loggedIn is set
    // on the user by the time Autocapture events fire.
    identify?: Identify
  }
  sessionReplay?: {
    sampleRate: number
    debugMode?: boolean
    experimental?: {
      useWebWorker: boolean
    }
  }
}

interface InitializeResult {
  initialized: boolean
  msg: string
}

interface Config {
  analytics?: {
    adc?: {
      amplitude?: {
        enabled?: boolean
        api_key?: string
      }
    }
  }
}

// Flat user property map applied via Identify. Restricted to primitives on
// purpose — flat properties keep Amplitude charts predictable.
type AmplitudeUserProperties = Record<string, string | number | boolean>

// Consumer-provided options for initializeAmplitude. Apps can register their
// own enrichment plugins (e.g. app-specific path normalization) and user
// properties (e.g. org segmentation flags) without the shared package
// knowing about app internals.
interface InitializeOptions {
  plugins?: EnrichmentPlugin[]
  userProperties?: AmplitudeUserProperties
}

interface AmplitudeFeatureFlagConfig {
  enabled?: boolean
  options?: AmplitudeConfig
}

const eventContextEnrichmentPlugin = (
  pageContext: PageAnalyticsContext
): EnrichmentPlugin => {
  return {
    name: 'pageview-enrichment',
    type: 'enrichment',

    async execute(event) {
      const props = pageContext.getContext()

      event.event_properties = {
        ...event.event_properties,
        ...props,
      }

      return event
    },
  }
}

// Firas asked for tracking for smartsale.
// smartsale was removed for blocked hosts.
const BLOCKED_HOSTS: string[] = []

const isBlockedHost = (hostname: string): boolean => {
  return BLOCKED_HOSTS.includes(hostname)
}

// TODO: not sure if this is the best way to do it
const hostnameFilterPlugin = (): EnrichmentPlugin => ({
  name: 'hostname-filter',
  type: 'enrichment',

  async execute(event) {
    const hostname = window.location.hostname

    if (isBlockedHost(hostname)) {
      return null // drop event
    }

    return event
  },
})

const DEFAULT_AMPLITUDE_CONFIG: AmplitudeConfig = {
  analytics: {
    remoteConfig: {
      fetchRemoteConfig: true,
    },
    defaultTracking: false,
  },
  sessionReplay: {
    sampleRate: 0.05,
    experimental: {
      useWebWorker: true,
    },
  },
}

const initializeAmplitude = async (
  config: Config,
  ctx: PageAnalyticsContext | null,
  options?: InitializeOptions
): Promise<InitializeResult> => {
  const result: InitializeResult = {
    initialized: false,
    msg: '',
  }
  const isAmplitudeEnabledInSSPConfig =
    config.analytics?.adc?.amplitude?.enabled

  if (!ctx) {
    logTracking('amplitude', 'analytics context missing')
    result.msg += 'Error initializing amplitude.'
    result.initialized = false
    return result
  }

  const featureFlags = ctx.getFeatureFlags?.()

  if (!featureFlags) {
    logTracking('amplitude', 'feature flags missing')
    result.msg += 'Error initializing amplitude.'
    result.initialized = false
    return result
  }

  const amplitudeFeatureFlagConfig: AmplitudeFeatureFlagConfig =
    featureFlags['fe.resi.enable-amplitude-tracking'] || {}

  const isAmplitudeEnabledInFeatureFlag = amplitudeFeatureFlagConfig.enabled

  const isAmplitudeEnabled =
    isAmplitudeEnabledInFeatureFlag && isAmplitudeEnabledInSSPConfig

  if (!isAmplitudeEnabled) {
    logTracking('amplitude', 'Disabled')
  }

  // Setup Amplitude
  if (isAmplitudeEnabled) {
    const amplitudeApiKey = config.analytics?.adc?.amplitude?.api_key

    const amplitudeOptions: AmplitudeConfig =
      amplitudeFeatureFlagConfig.options || { ...DEFAULT_AMPLITUDE_CONFIG }

    const userId = ctx.getUserId()

    if (!amplitudeOptions.analytics) {
      amplitudeOptions.analytics = {}
    }

    if (userId) {
      amplitudeOptions.analytics.userId = userId
    } else {
      // Clear any persisted userId from a previous session
      amplitudeOptions.analytics.userId = undefined
    }

    amplitudeOptions.analytics.deviceId = Cookies.get('BRID') || undefined

    try {
      amplitude.add(eventContextEnrichmentPlugin(ctx))
      amplitude.add(hostnameFilterPlugin())

      // Consumer-provided plugins, registered before initAll so they apply
      // from session_start onward.
      options?.plugins?.forEach(plugin => amplitude.add(plugin))

      // Set loggedIn BEFORE init so it fires ahead of session_start.
      // initAll spreads options.analytics into the browser init config, and
      // the browser client tracks config.identify before the
      // DEFAULT_SESSION_START_EVENT — so loggedIn resolves on session_start
      // and the first Page Viewed instead of coming through as 'None'.
      const identify = new Identify()
      identify.set('loggedIn', Boolean(userId))

      // Consumer-provided user properties, set on the same pre-init
      // Identify so they resolve from session_start onward.
      Object.entries(options?.userProperties || {}).forEach(([key, value]) =>
        identify.set(key, value)
      )

      amplitudeOptions.analytics.identify = identify

      await amplitude.initAll(
        amplitudeApiKey as string,
        amplitudeOptions as any
      )
      result.initialized = true

      // Explicitly sync userId state — clears persisted identity on logout
      if (userId) {
        amplitude.setUserId(userId)
      } else {
        amplitude.setUserId(undefined)
      }

      logTracking('amplitude', `${JSON.stringify(amplitudeOptions)}`)
    } catch (err) {
      result.msg += 'Error initializing amplitude.'
      result.initialized = false
      // eslint-disable-next-line no-console
      console.error(err)
    }

    return result
  }

  result.msg += 'Amplitude tracking disabled'
  result.initialized = false
  return result
}

/**
 * Update Amplitude user identity after client-side authentication.
 * Call when a user logs in or out without a full page reload.
 * Optional user properties are applied on the same Identify call.
 */
const identifyUser = (
  userId: string | null,
  userProperties?: AmplitudeUserProperties
): void => {
  const identify = new Identify()

  if (userId) {
    amplitude.setUserId(userId)
    identify.set('loggedIn', true)
  } else {
    amplitude.setUserId(undefined)
    identify.set('loggedIn', false)
  }

  Object.entries(userProperties || {}).forEach(([key, value]) =>
    identify.set(key, value)
  )

  amplitude.identify(identify)
}

export {
  initializeAmplitude,
  createAnalyticsContext,
  identifyUser,
  PageAnalyticsContext,
}
export type { EnrichmentPlugin, InitializeOptions, AmplitudeUserProperties }
